import { Link, useNavigate } from 'react-router-dom';
import { useFavorites } from '../favorites/FavoritesContext';
import type { Animation } from '../types';

export default function AnimationCard({ animation }: { animation: Animation }) {
  const navigate = useNavigate();
  const { favoritedIds, likesById, toggleFavorite } = useFavorites();
  // author は型上必須だが、旧データ・移行漏れ・外部クライアント投稿で欠けることがある。
  // 描画中の例外は React ツリー全体を落とす（画面が真っ白になる）ため必ずフォールバックする。
  const authorName = animation.author ?? '不明なユーザー';
  const authorInitial = authorName.slice(0, 1).toUpperCase();
  const favorited = favoritedIds.has(animation.id);
  const likes = likesById[animation.id] ?? animation.likes;

  // カード全体がリンクのため、作者名やいいねボタンだけ別の挙動をさせるには
  // Link を入れ子にできない（HTML的に無効）。クリックを奪って preventDefault +
  // stopPropagation する要素に切り替える（作者名は navigate、いいねは toggleFavorite）。
  const handleAuthorClick = (e: React.MouseEvent) => {
    if (!animation.ownerId) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(`/users/${animation.ownerId}`);
  };

  const handleLikeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(animation.id, likes);
  };

  return (
    <Link to={`/animations/${animation.id}`} className="card">
      <div className="card__thumb">
        {animation.thumbnailUrl ? (
          <img src={animation.thumbnailUrl} alt={animation.title} />
        ) : (
          <span className="card__ini">{animation.title.slice(0, 1)}</span>
        )}
      </div>
      <div className="card__body">
        <div className="card__title">
          {animation.title}
          {animation.visibility === 'private' && (
            <span className="badge badge--private">非公開</span>
          )}
        </div>
        <div className="card__meta">
          <span
            className={`card__author${animation.ownerId ? ' card__author-link' : ''}`}
            role={animation.ownerId ? 'link' : undefined}
            onClick={handleAuthorClick}
          >
            <span className="card__ava">{authorInitial}</span>
            <span className="card__author-name">{authorName}</span>
          </span>
          <button
            type="button"
            className={`card__like${favorited ? ' on' : ''}`}
            aria-pressed={favorited}
            aria-label="いいね"
            onClick={handleLikeClick}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={favorited ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={2}
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 21s-6.7-4.35-9.33-8.24C1.02 10.4 1.6 7 4.5 5.6 6.68 4.55 9 5.3 12 8.2c3-2.9 5.32-3.65 7.5-2.6 2.9 1.4 3.48 4.8 1.83 7.16C18.7 16.65 12 21 12 21z" />
            </svg>
            {likes}
          </button>
        </div>
        {/* author が無ければクレジット行自体を出さない。バッジはカードに出さない。 */}
        {animation.credit?.author && (
          <div className="card__credit">出典: {animation.credit.author}</div>
        )}
      </div>
    </Link>
  );
}
